export interface DashboardCategory {
  id: number;
  name: string;
  slug: string;
  // Not confirmed live yet (docs/needed-endpoints.md §9/§11) - colors the category's collapsible
  // arrow on the Dashboards page (CollapsibleDashboardGroup.tsx), not the same thing as
  // CATEGORY_COLORS (categoryColors.ts)'s client-side name-keyed map used elsewhere.
  color?: string | null;
}

// Same shape as DashboardCategory - kept as a distinct name at call sites for clarity, since
// `tags` and `categories` serve two different purposes (see SearchableDashboard's own comment)
// even though they're mirrored values today.
export type DashboardTag = DashboardCategory;

// Real shape of a GET /dashboards row (docs/needed-endpoints.md §1). `key` is the dashboard's
// stable string identifier (e.g. "brand_performance") - it's what every other mock/store in this
// app that needs a string-keyed join (favourites localStorage, engagement mocks, access-request
// mocks, team-request mocks) should use, NOT the numeric `id`, which is the real database id and
// currently has no other consumer in this codebase.
export interface SearchableDashboard {
  id: number;
  key: string;
  name: string;
  description: string;
  path: string;
  icon: string | null;
  hide_on_mobile: boolean;
  supports_export: boolean;
  engagement_url: string | null;
  // `categories`: which section(s) a dashboard is grouped under on the Dashboards page (Use/
  // Experience/Shop, admin-managed via "Add Category"/"Add Dashboard") - page organization.
  categories: DashboardCategory[];
  // `tags`: shown per-dashboard on its card (multiple pills) and as a compact "{name} - {tag}"
  // suffix in other dashboard lists/dropdowns - display, not organization. A real backend field
  // (docs/needed-endpoints.md §1) - unlike `categories`, populated from the start with the
  // original 6-group scheme (Experience/Shop/Use/Executive Reporting/Analytics Tools/Archived).
  // `categories` is admin-managed separately from a blank slate via "Add Category"/"Add
  // Dashboard", so it starts empty until an admin assigns dashboards to it.
  tags: DashboardTag[];
  // Confirmed against the backend's own DashboardKPITermSummarySchema - a list of KPI-term
  // records ({term, description, external_ref}, each nullable), not bare strings. DashboardSearchBar.tsx
  // matches against each entry's `.term` field.
  matched_terms: DashboardMatchedTerm[];
  is_active: boolean;
  has_access: boolean;
}

export interface DashboardMatchedTerm {
  term: string | null;
  description: string | null;
  external_ref: string | null;
}

// Every dashboard has exactly one category today (the M:N `categories` shape is future-proofing,
// per docs/dashboard-registry-seed-checklist.md decision #1) - this is the one place that "picks"
// a single representative category for the few places in this app that still group/color by a
// single category name (DashboardCard's description lookup, the Dashboards/Settings page-grouping
// logic), so that assumption only needs revisiting in one spot if it ever stops holding.
export const getPrimaryCategory = (dashboard: SearchableDashboard): string => dashboard.categories[0]?.name ?? "Uncategorized";

// A dashboard is "archived" in either of two senses that mean the same thing to every caller here:
// is_active: false (the original flag), or assigned to the "Archived" organizational category
// (slug "archived") while still is_active: true - per docs/dashboard-registry-seed-checklist.md's
// own "is_active ≠ archived" note, some dashboards are seeded that way on purpose, kept navigable
// at their real route but grouped under this label instead of their old menu placement. Either way,
// a dashboard this returns true for belongs only on the dedicated /archived page (ArchivedPage.tsx)
// - not mixed into the Dashboards page's own category groups, Settings' Engagement section, or
// Settings' My Dashboard Access section.
export const isArchivedDashboard = (dashboard: SearchableDashboard): boolean =>
  !dashboard.is_active || dashboard.categories.some((category) => category.slug === "archived");

// Shared by the Home page search bar (DashboardSearchBar.tsx) and the Dashboards page search bar
// (DashboardsPage.tsx) - a dashboard matches a search term if it appears in the name, the
// dashboard's own description, any of its assigned categories' names, or any of its KPI
// matched_terms' own `.term`, `.description`, OR `.external_ref` field (each entry is a {term,
// description, external_ref} record, not a bare string - see DashboardMatchedTerm above - and any
// field can be null). `term` is expected pre-trimmed and lowercased by the caller (both call sites
// already compute this once per keystroke rather than per-dashboard).
export const dashboardMatchesSearchTerm = (dashboard: SearchableDashboard, trimmedLowerTerm: string): boolean => {
  if (!trimmedLowerTerm) return true;
  if (dashboard.name.toLowerCase().includes(trimmedLowerTerm)) return true;
  if (dashboard.description.toLowerCase().includes(trimmedLowerTerm)) return true;
  if (dashboard.categories?.some((category) => category.name.toLowerCase().includes(trimmedLowerTerm))) return true;
  return (
    dashboard.matched_terms?.some(
      (entry) =>
        entry.term?.toLowerCase().includes(trimmedLowerTerm) ||
        entry.description?.toLowerCase().includes(trimmedLowerTerm) ||
        entry.external_ref?.toLowerCase().includes(trimmedLowerTerm)
    ) ?? false
  );
};
